import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { Snackbar, Alert, type AlertColor } from '@mui/material';
import { useSocket } from './SocketContext.js';

interface Notification {
  id: string;
  message: string;
  severity: AlertColor;
  autoHideDuration?: number;
}

interface NotificationContextType {
  notify: (message: string, severity?: AlertColor) => void;
  notifications: Notification[];
}

const NotificationContext = createContext<NotificationContextType>({
  notify: () => {},
  notifications: [],
});

let notificationId = 0;

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [current, setCurrent] = useState<Notification | null>(null);
  const { socket } = useSocket();

  const notify = useCallback((message: string, severity: AlertColor = 'info') => {
    const notification: Notification = {
      id: String(++notificationId),
      message,
      severity,
      autoHideDuration: severity === 'error' ? 8000 : 4000,
    };
    setNotifications((prev) => [...prev, notification]);
    setCurrent(notification);
  }, []);

  // Socket.io Notifications
  useEffect(() => {
    if (!socket) return;

    socket.on('notification', (data: { message: string; type?: AlertColor }) => {
      notify(data.message, data.type || 'info');
    });

    return () => {
      socket.off('notification');
    };
  }, [socket, notify]);

  const handleClose = useCallback(() => {
    setCurrent(null);
  }, []);

  return (
    <NotificationContext.Provider value={{ notify, notifications }}>
      {children}
      <Snackbar
        open={!!current}
        autoHideDuration={current?.autoHideDuration ?? 4000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {current ? (
          <Alert onClose={handleClose} severity={current.severity} variant="filled" sx={{ width: '100%' }}>
            {current.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  return useContext(NotificationContext);
}
